import { useCallback, useEffect, useRef, useState } from 'react';

export type UseImageResizeOptions = {
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  maxWidth?: number;
  debounceMs?: number;
  onChange: (size: { width: number; height: number }) => void;
};

type StartDragEvent = { clientX: number; preventDefault?: () => void };

export function useImageResize(opts: UseImageResizeOptions) {
  const {
    initialWidth,
    initialHeight,
    minWidth = 100,
    maxWidth = 4000,
    debounceMs = 200,
  } = opts;
  const aspectRatio = initialWidth / initialHeight;

  const [width, setWidth] = useState(initialWidth);
  const widthRef = useRef(width);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const directionRef = useRef<'left' | 'right'>('right');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(opts.onChange);

  useEffect(() => {
    widthRef.current = width;
  });
  useEffect(() => {
    onChangeRef.current = opts.onChange;
  });

  const fireOnChange = useCallback(
    (w: number) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      const payload = {
        width: w,
        height: Math.round(w / aspectRatio),
      };
      if (debounceMs <= 0) {
        onChangeRef.current(payload);
        return;
      }
      debounceTimer.current = setTimeout(() => {
        onChangeRef.current(payload);
      }, debounceMs);
    },
    [aspectRatio, debounceMs],
  );

  const startDrag = useCallback(
    (e: StartDragEvent, dir: 'left' | 'right') => {
      e.preventDefault?.();
      startXRef.current = e.clientX;
      startWidthRef.current = widthRef.current;
      directionRef.current = dir;

      function onMove(ev: MouseEvent) {
        const delta = ev.clientX - startXRef.current;
        const signed = directionRef.current === 'right' ? delta : -delta;
        const next = Math.max(
          minWidth,
          Math.min(maxWidth, startWidthRef.current + signed),
        );
        setWidth(next);
        fireOnChange(next);
      }

      function onUp() {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      }

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [fireOnChange, maxWidth, minWidth],
  );

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    [],
  );

  return { width, startDrag };
}
